import React from 'react';
import { WidgetHeaderIcon } from '@/shared/components/ui/WidgetHeaderIcon';
import type { LeadConversionByPeriod } from '@troia-v3/types';
interface LeadConversionChartProps {
  data: LeadConversionByPeriod[];
  title?: string;
}

/**
 * Gráfico de conversão de leads por período
 *
 * Segue o padrão visual dos widgets da dashboard principal:
 * - Header compacto com WidgetHeaderIcon
 * - Legenda inline com dots coloridos
 * - tabular-nums para valores numéricos
 */
export const LeadConversionChart: React.FC<LeadConversionChartProps> = ({
  data,
  title = 'Conversão por Período',
}) => {
  if (!data || data.length === 0) {
    return (
      <div
        className="rounded-lg border p-8 text-center"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <p style={{ color: 'var(--theme-muted-text)' }}>
          Sem dados de conversão
        </p>
      </div>
    );
  }

  // Calculate max values for scaling
  const maxTotal = Math.max(...data.map(d => d.totalLeads));

  const formatDate = (period: string): string => {
    const parts = period.split('-');
    if (parts.length === 3) {
      // YYYY-MM-DD format
      return `${parts[2]}/${parts[1]}`;
    }
    if (parts.length === 2) {
      // YYYY-MM format
      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return months[parseInt(parts[1]) - 1] || parts[1];
    }
    return period;
  };

  return (
    <div
      className="rounded-lg border"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Header */}
      <div className="px-4 py-3 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h3
              className="text-sm font-semibold"
              style={{ color: 'var(--theme-card-text)' }}
            >
              {title}
            </h3>
            <p
              className="text-xs mt-0.5"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Evolução de leads por período
            </p>
          </div>
          <WidgetHeaderIcon icon={'trending-up'} />
        </div>
      </div>

      {/* Chart Area */}
      <div className="px-4 pb-4">
        {/* Legend */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1.5">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: 'var(--theme-success-color)' }}
            />
            <span
              className="text-[11px]"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Ganhos
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: 'var(--theme-error-color)' }}
            />
            <span
              className="text-[11px]"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Perdidos
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: 'var(--theme-info-color)' }}
            />
            <span
              className="text-[11px]"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Em Progresso
            </span>
          </div>
        </div>

        {/* Bar Chart */}
        <div className="space-y-3">
          {data.map((item) => {
            const totalHeight = maxTotal > 0 ? (item.totalLeads / maxTotal) * 100 : 0;
            const wonPercent = item.totalLeads > 0 ? (item.leadsWon / item.totalLeads) * 100 : 0;
            const lostPercent = item.totalLeads > 0 ? (item.leadsLost / item.totalLeads) * 100 : 0;
            const progressPercent = 100 - wonPercent - lostPercent;

            return (
              <div key={item.period} className="flex items-center gap-3">
                {/* Period Label (mockup `.p{width:42px}`) */}
                <div
                  className="w-[42px] text-xs text-right"
                  style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatDate(item.period)}
                </div>

                {/* Stacked Bar (mockup `.lc-bar{height:18px;border-radius:5px}`, largura proporcional exata) */}
                <div className="flex-1">
                  <div
                    className="h-[18px] rounded-[5px] flex overflow-hidden"
                    style={{
                      width: `${totalHeight}%`,
                      backgroundColor: 'var(--theme-card-border)',
                    }}
                  >
                    {/* Won */}
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${wonPercent}%`,
                        backgroundColor: 'var(--theme-success-color)',
                      }}
                      title={`Ganhos: ${item.leadsWon}`}
                    />
                    {/* In Progress */}
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${progressPercent}%`,
                        backgroundColor: 'var(--theme-info-color)',
                      }}
                      title={`Em Progresso: ${item.leadsInProgress}`}
                    />
                    {/* Lost */}
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${lostPercent}%`,
                        backgroundColor: 'var(--theme-error-color)',
                      }}
                      title={`Perdidos: ${item.leadsLost}`}
                    />
                  </div>
                </div>

                {/* Stats (mockup `.n{width:64px}` · `.cv{width:40px;font-weight:700}`) */}
                <div
                  className="w-16 text-right text-xs"
                  style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {item.totalLeads} leads
                </div>
                <div
                  className="w-10 text-right text-xs font-bold"
                  style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {item.conversionRate.toFixed(0)}%
                </div>
              </div>
            );
          })}
        </div>

        {/* Total Value Summary */}
        <div
          className="mt-4 pt-3 border-t flex items-center justify-between"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          <span
            className="text-xs"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Valor total no período
          </span>
          <span
            className="text-xs font-semibold"
            style={{ color: 'var(--theme-success-color)', fontVariantNumeric: 'tabular-nums' }}
          >
            {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(data.reduce((acc, d) => acc + d.totalValue, 0))}
          </span>
        </div>
      </div>
    </div>
  );
};

LeadConversionChart.displayName = 'LeadConversionChart';
